import { useEffect, useRef } from 'react';

/**
 * CodeInjection component that loads and injects custom code from admin settings
 * - head_code: Injected into <head> tag (e.g., analytics, meta tags)
 * - body_code: Injected at start of <body> (e.g., noscript, header scripts)
 * - footer_code: Injected at end of <body> (e.g., tracking pixels, footer scripts)
 */
const CodeInjection = () => {
  const hasInjected = useRef(false);

  useEffect(() => {
    // Only load once - check both ref and DOM
    if (hasInjected.current) return;
    if (document.getElementById('ff-body-injection') || document.getElementById('ff-footer-injection')) {
      hasInjected.current = true;
      return;
    }

    const loadCodeInjection = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/code-injection`);
        if (!response.ok) return;
        
        const data = await response.json();
        
        // Inject head code
        if (data.head_code) {
          // Check if already injected
          if (!document.getElementById('ff-head-injection')) {
            const headContainer = document.createElement('div');
            headContainer.id = 'ff-head-injection';
            headContainer.innerHTML = data.head_code;
            
            // Move script and style elements to head
            const headElements = headContainer.querySelectorAll('script, style, link, meta');
            headElements.forEach(el => {
              const clone = el.cloneNode(true);
              // For scripts, we need to recreate them to execute
              if (el.tagName === 'SCRIPT') {
                const script = document.createElement('script');
                script.setAttribute('data-ff-injected', 'true');
                Array.from(el.attributes).forEach(attr => {
                  script.setAttribute(attr.name, attr.value);
                });
                script.textContent = el.textContent;
                document.head.appendChild(script);
              } else {
                clone.setAttribute('data-ff-injected', 'true');
                document.head.appendChild(clone);
              }
            });
          }
        }
        
        // Inject body code (at start of body)
        if (data.body_code && !document.getElementById('ff-body-injection')) {
          const bodyContainer = document.createElement('div');
          bodyContainer.id = 'ff-body-injection';
          bodyContainer.innerHTML = data.body_code;
          
          // Insert at beginning of body, after any existing scripts
          if (document.body.firstChild) {
            document.body.insertBefore(bodyContainer, document.body.firstChild);
          } else {
            document.body.appendChild(bodyContainer);
          }
          
          // Execute scripts
          const scripts = bodyContainer.querySelectorAll('script');
          scripts.forEach(oldScript => {
            const newScript = document.createElement('script');
            Array.from(oldScript.attributes).forEach(attr => {
              newScript.setAttribute(attr.name, attr.value);
            });
            newScript.textContent = oldScript.textContent;
            oldScript.parentNode.replaceChild(newScript, oldScript);
          });
        }
        
        // Inject footer code (at end of body)
        if (data.footer_code && !document.getElementById('ff-footer-injection')) {
          const footerContainer = document.createElement('div');
          footerContainer.id = 'ff-footer-injection';
          footerContainer.innerHTML = data.footer_code;
          document.body.appendChild(footerContainer);
          
          // Execute scripts
          const scripts = footerContainer.querySelectorAll('script');
          scripts.forEach(oldScript => {
            const newScript = document.createElement('script');
            Array.from(oldScript.attributes).forEach(attr => {
              newScript.setAttribute(attr.name, attr.value);
            });
            newScript.textContent = oldScript.textContent;
            oldScript.parentNode.replaceChild(newScript, oldScript);
          });
        }
        
        hasInjected.current = true;
      } catch (error) {
        console.error('Failed to load code injection settings:', error);
      }
    };

    loadCodeInjection();
  }, []);

  // This component doesn't render anything visible
  return null;
};

export default CodeInjection;
